import { KatalComponent } from '../../shared/base';
import { roundStars } from './star-rating-calculator';

const FULL_STAR = 'full-star';
const HALF_STAR = 'half-star';
const EMPTY_STAR = 'empty-star';

/**
 * @component {kat-star-rating} Star Rating is a common Amazon affordance that allows customers to rate their experience.
 * @guideline Do Star ratings can be filled in half star or full star increments.
 * @guideline Dont Do not use gradients or change the fill colors
 * @guideline Do Do use the input variant to allow users to select a rating.
 * @guideline Do Do use the default variant for to display a rating.
 * @guideline Dont StarRating and StarRatingWithLink variants are to be used for display purposes only. StarRatingInput and StarRatingInputWithError are to be used only as inputs (not for display)
 * @example StarRating {"rating": "4.5","review-label":"236"}
 * @example StarRatingWithLink {"rating": "2","review-label":"236","href":"https://www.amazon.com"}
 * @example StarRatingInput {"variant": "input"}
 * @example StarRatingInputWithError {"rating": "2","variant": "input", "error-label": "Please choose a rating"}
 * @event change Fires when the user selects a different rating.
 * @status Production
 * @theme flo
 */
export class KatalStarRating extends KatalComponent {
  static get observedAttributes() {
    return [
      'rating',
      'review-label',
      'error-label',
      'href',
      'stars',
      'variant',
      'name',
    ];
  }

  constructor() {
    super(KatalStarRating.observedAttributes);
    this.loaded = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.loaded = true;
    this.render();

    this.addEventListener('click', evt => {
      if (this.variant !== 'input') {
        return;
      }

      const rating = this._getRatingFromStarEvent(evt);
      if (rating === 0 || rating === parseFloat(this.rating)) {
        return;
      }
      this.rating = rating;
      this.dispatchCustomEvent('change', { rating: rating });
    });
  }

  /**
   * @classprop {string} rating This is the score that will be displayed as full stars and half-stars out of the total number of stars
   * @required
   */
  get rating() {
    return this.getAttribute('rating') || '0';
  }

  set rating(value) {
    let parsed = parseFloat(value);
    if (!isNaN(parsed)) {
      this.setAttribute('rating', value);
    } else {
      console.error('Rating must be parsable to an integer');
    }
  }

  /**
   * @classprop {string} review-label This is the review label
   */
  get reviewLabel() {
    return this.getAttribute('review-label') || '';
  }

  set reviewLabel(value) {
    this.setAttribute('review-label', value);
  }

  /**
   * @classprop {string} error-label This is the text to show if there is an error.
   */
  get errorLabel() {
    return this.getAttribute('error-label') || '';
  }

  set errorLabel(value) {
    this.setAttribute('error-label', value);
  }

  /**
   * @classprop {string} href This will make the star rating component a clickable link
   */
  get href() {
    return this.getAttribute('href') || '';
  }

  set href(value) {
    this.setAttribute('href', value);
  }

  get stars() {
    return this.getAttribute('stars') || '5';
  }

  set stars(value) {
    let parsed = parseInt(value, 10);
    if (!isNaN(parsed)) {
      this.setAttribute('stars', value);
    } else {
      console.error('Stars must be parsable to an integer');
    }
  }

  /**
   * @classprop {enum} variant Defines if the component can be used to display or set a rating.
   * @enum {value} default Component is dispaly only.
   * @enum {value} input Component is used to select star rating.
   */
  get variant() {
    return this.getAttribute('variant') || 'default';
  }
  set variant(value) {
    this.setAttribute('variant', value);
  }

  /**
   * @classprop {string} name The name of this as an input inside a form.
   */
  get name() {
    return this.getAttribute('name') || '';
  }
  set name(value) {
    this.setAttribute('name', value);
  }

  render() {
    this.innerHTML = '';
    const starsElement = this._createStarsElement();
    const review = document.createElement('span');
    review.setAttribute('class', 'review');
    review.innerText = this.reviewLabel;

    let firstLine = document.createElement('span');
    if (this.href !== '') {
      const link = document.createElement('a');
      link.setAttribute('href', this.href);
      link.setAttribute('target', '_blank');

      if (this.variant === 'input') {
        link.appendChild(review);
        firstLine.appendChild(starsElement);
        firstLine.appendChild(link);
      } else {
        link.appendChild(starsElement);
        link.appendChild(review);
        firstLine = link;
      }
    } else {
      firstLine.appendChild(starsElement);
      firstLine.appendChild(review);
    }

    firstLine.setAttribute('class', 'first-line');
    this.appendChild(firstLine);

    if (this.errorLabel !== '') {
      const error = document.createElement('kat-label');
      error.setAttribute('class', 'error-label');
      error.setAttribute('variant', 'constraint');
      error.setAttribute('text', this.errorLabel);
      error.setAttribute('state', 'error');
      this.appendChild(error);
    }

    let hiddenInput = '';
    if (this.name !== '') {
      const hiddenInput = document.createElement('input');
      hiddenInput.setAttribute('type', 'hidden');
      hiddenInput.setAttribute('name', this.name);
      hiddenInput.setAttribute('value', this.rating);
      this.appendChild(hiddenInput);
    }
  }

  _createStarsElement() {
    const numStars = parseInt(this.stars, 10);
    const calculateStarRating = roundStars(this.rating);
    const stars = document.createElement('span');
    stars.setAttribute('class', 'stars');
    for (let i = 1; i <= numStars; i++) {
      let icon;
      if (i <= calculateStarRating.fullStarCount) {
        icon = this._createStar(FULL_STAR);
      } else if (
        calculateStarRating.hasHalfStar &&
        i == calculateStarRating.fullStarCount + 1
      ) {
        icon = this._createStar(HALF_STAR);
      } else {
        icon = this._createStar(EMPTY_STAR);
      }
      stars.appendChild(icon);
    }
    return stars;
  }

  _createGradientDefinition() {
    return this._createSvgElement('defs', {}, [
      this._createSvgElement(
        'linearGradient',
        {
          x1: '50%',
          y1: '0%',
          x2: '50%',
          y2: '100%',
          id: 'katal-stars-linear-gradient',
        },
        [
          this._createSvgElement('stop', {
            offset: '0%',
            'stop-color': '#FED007',
          }),
          this._createSvgElement('stop', {
            offset: '100%',
            'stop-color': '#FEB502',
          }),
        ]
      ),
    ]);
  }

  _createStar(type) {
    const gradientFill = 'url(#katal-stars-linear-gradient)';
    const halfPolygon = this._createSvgElement('polygon', {
      points: '12,13.8 8.6,15.7 9.3,11.9 6.6,9.2 10.3,8.7 12,5.3',
      fill: gradientFill,
    });
    return this._createSvgElement(
      'svg',
      {
        viewBox: '5 4 14 14',
        class: 'katal-starrating-' + type,
      },
      [
        type === EMPTY_STAR ? null : this._createGradientDefinition(),
        this._createSvgElement('g', { 'pointer-events': 'bounding-box' }, [
          this._createSvgElement('path', {
            d:
              'M16,16.6l-0.8-4.5l3.3-3.2L14,8.2l-2-4.1l-2,4.1L5.5,8.9l3.3,3.2L8,16.6l4-2.1L16,16.6z',
            stroke: '#C88702',
            fill: type === FULL_STAR ? gradientFill : '#FFFFFF',
          }),
          type === HALF_STAR ? halfPolygon : null,
        ]),
      ]
    );
  }

  _createSvgElement(tagName, attributes, children) {
    var element = document.createElementNS(
      'http://www.w3.org/2000/svg',
      tagName
    );
    if (attributes) {
      Object.keys(attributes).forEach(attr => {
        element.setAttribute(attr, attributes[attr]);
      });
    }
    if (children) {
      children.forEach(child => {
        if (child) {
          element.appendChild(child);
        }
      });
    }
    return element;
  }

  // Respond to attribute changes.
  attributeChangedCallback(attr, oldValue, newValue) {
    if (this.loaded) {
      this.render();
    }
  }

  _getRatingFromStarEvent(evt) {
    const icon = this._getAncestor(evt.target, 'svg');
    if (!icon) {
      return 0;
    }

    let numChild = 0;
    const children = icon.parentNode.children;
    for (let i = 0; i < children.length; i++) {
      if (icon === children[i]) {
        numChild = i;
        break;
      }
    }

    return numChild + 1;
  }

  _getAncestor(child, tagName) {
    if (child.tagName.toUpperCase() === tagName.toUpperCase()) {
      return child;
    } else if (
      child.tagName === 'KAT-STAR-RATING' ||
      child.parentNode === null
    ) {
      return null;
    } else {
      return this._getAncestor(child.parentNode, tagName);
    }
  }
}

KatalComponent.register('kat-star-rating', KatalStarRating);
